import {
  AffiliationEndReason,
  AffiliationStatus,
} from "@api/shared-runtime/enums";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AFFILIATION_END_REASON_LABELS } from "./affiliation-end-reason-labels";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type ProviderRosterStatus = AffiliationStatus | "ended";

const STATUS_OPTIONS: Array<{
  value: ProviderRosterStatus;
  label: string;
  description: string;
}> = [
  {
    value: AffiliationStatus.Candidate,
    label: "Candidate",
    description: "Keep on the provider list without the hired designation.",
  },
  {
    value: AffiliationStatus.Hired,
    label: "Hired",
    description: "Show as actively hired by this organization.",
  },
  {
    value: "ended",
    label: "Ended",
    description: "Remove from the active provider list and retain history.",
  },
];

const HIRED_END_REASONS = [
  AffiliationEndReason.MarkedInError,
  AffiliationEndReason.Terminated,
  AffiliationEndReason.Resigned,
] as const;

const CANDIDATE_END_REASONS = [
  AffiliationEndReason.MarkedInError,
  AffiliationEndReason.Rejected,
  AffiliationEndReason.Withdrawn,
] as const;

export function ChangeProviderStatusDialog({
  open,
  providerName,
  currentStatus,
  canMarkHired,
  selectedStatus,
  startDate,
  endReason,
  isPending,
  onOpenChange,
  onStatusChange,
  onStartDateChange,
  onEndReasonChange,
  onCancel,
  onConfirm,
  onRestoreFocus,
}: {
  open: boolean;
  providerName: string | undefined;
  currentStatus: AffiliationStatus | undefined;
  canMarkHired: boolean;
  selectedStatus: ProviderRosterStatus;
  startDate: string;
  endReason: AffiliationEndReason;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (status: ProviderRosterStatus) => void;
  onStartDateChange: (value: string) => void;
  onEndReasonChange: (reason: AffiliationEndReason) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onRestoreFocus?: () => void;
}) {
  const needsEndReason = selectedStatus === "ended";
  const endReasons =
    currentStatus === AffiliationStatus.Hired
      ? HIRED_END_REASONS
      : CANDIDATE_END_REASONS;
  const statusUnchanged =
    selectedStatus === currentStatus &&
    selectedStatus !== AffiliationStatus.Hired;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onCloseAutoFocus={(event) => {
          if (!onRestoreFocus) {
            return;
          }
          event.preventDefault();
          onRestoreFocus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Change provider status</DialogTitle>
          <DialogDescription>
            Choose how
            <span className="font-medium text-foreground">
              {" "}
              {providerName}
            </span>{" "}
            should appear in your organization.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={selectedStatus}
          onValueChange={(value) =>
            onStatusChange(value as ProviderRosterStatus)
          }
          className="gap-3"
        >
          {STATUS_OPTIONS.map((option) => (
            <Label
              key={option.value}
              htmlFor={"provider-status-" + option.value}
              className="flex cursor-pointer items-start gap-3 rounded-md border p-3 has-[[data-state=checked]]:border-primary"
            >
              <RadioGroupItem
                id={"provider-status-" + option.value}
                value={option.value}
                className="mt-0.5"
                disabled={
                  option.value === AffiliationStatus.Hired &&
                  !canMarkHired &&
                  currentStatus !== AffiliationStatus.Hired
                }
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">{option.label}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {option.description}
                </span>
              </span>
            </Label>
          ))}
        </RadioGroup>

        {selectedStatus === AffiliationStatus.Hired && (
          <div className="space-y-1.5">
            <Label htmlFor="provider-status-start-date">
              Start date (optional)
            </Label>
            <Input
              id="provider-status-start-date"
              type="date"
              value={startDate}
              onChange={(event) => onStartDateChange(event.target.value)}
            />
          </div>
        )}

        {needsEndReason && (
          <div className="space-y-1.5">
            <Label htmlFor="provider-status-end-reason">
              Reason for change
            </Label>
            <Select
              value={endReason}
              onValueChange={(value) =>
                onEndReasonChange(value as AffiliationEndReason)
              }
            >
              <SelectTrigger id="provider-status-end-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {endReasons.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {AFFILIATION_END_REASON_LABELS[reason]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <DialogFooter className="border-t pt-4 sm:space-x-0">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={selectedStatus === "ended" ? "destructive" : "default"}
            disabled={isPending || statusUnchanged}
            onClick={onConfirm}
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </span>
            ) : (
              "Save status"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
